import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth0 } from '../../hooks/useAuth0';
import {
  Box, Container, Typography, Paper, Button, TextField,
  Chip, CircularProgress, Alert, Divider, IconButton,
  Tooltip, Accordion, AccordionSummary, AccordionDetails,
  Checkbox, FormControlLabel
} from '@mui/material';
import {
  Download, Save, Add, Delete, ExpandMore, 
  MedicalServices, QuestionAnswer, Warning,
  Medication, FavoriteRounded, Print
} from '@mui/icons-material';
import api from '../../services/api';

// ── Question bank by type and organ ──────────────────────────────────────────

const QUESTION_BANK = {
  AL: {
    general: [
      "What is my current serum free light chain level and ratio?",
      "What is my hematologic response — complete, very good partial, or partial?",
      "Am I a candidate for autologous stem cell transplant (ASCT)?",
      "Should I consider a clinical trial for AL amyloidosis?",
      "How often should I have bone marrow biopsies?",
      "What is my current treatment regimen and how long will I be on it?",
      "What are the signs that my treatment is working?",
      "What are the warning signs of disease progression I should watch for?",
    ],
    cardiac: [
      "What is my current NT-proBNP or BNP level and what does it mean?",
      "What is my current troponin level?",
      "Should I be seeing a cardiologist who specializes in amyloid cardiomyopathy?",
      "Do I need an echocardiogram and how often?",
      "Are there any medications I should avoid because of my heart involvement?",
      "Am I at risk for arrhythmia and should I have a Holter monitor?",
      "Should I consider an ICD or pacemaker?",
    ],
    renal: [
      "What is my current 24-hour urine protein level?",
      "What is my eGFR and what does the trend look like?",
      "Should I see a nephrologist in addition to my hematologist?",
      "At what point should I consider dialysis planning?",
      "Are there dietary changes I should make to protect my kidneys?",
      "Am I a candidate for kidney transplant if needed?",
    ],
    neurological: [
      "Should I have nerve conduction studies done?",
      "Is my neuropathy progressing and how do we measure that?",
      "What treatments are available for my neuropathy symptoms?",
      "Should I see a neurologist who specializes in amyloid neuropathy?",
    ],
    liver: [
      "What are my current liver enzyme levels?",
      "How enlarged is my liver and is it progressing?",
      "Are there any medications or supplements I should avoid?",
    ],
  },
  ATTR: {
    general: [
      "Am I eligible for Attruby (acoramidis) or tafamidis (Vyndaqel/Vyndamax)?",
      "Should I have genetic testing to determine if my ATTR is hereditary?",
      "If hereditary, should my family members be tested?",
      "What is my current TTR stabilization level?",
      "Am I eligible for any ATTR clinical trials, including ACT-EARLY?",
      "How often should I have follow-up imaging and labs?",
      "What is the difference between wild-type and hereditary ATTR for my prognosis?",
    ],
    cardiac: [
      "What is my current NT-proBNP level and trend?",
      "What stage of ATTR cardiomyopathy am I in (Mayo Stage 1, 2, or 3)?",
      "Should I be seeing a cardiac amyloidosis specialist?",
      "Do I need a bone scintigraphy scan (PYP or DPD)?",
      "Am I at risk for atrial fibrillation and how should it be managed?",
      "Should I be on anticoagulation therapy?",
      "Are there any medications I should stop because of ATTR-CM?",
    ],
    neurological: [
      "Is my neuropathy from ATTR and how severe is it?",
      "Am I eligible for patisiran or vutrisiran (RNA-interference therapies)?",
      "Should I see a neurologist who specializes in hereditary ATTR?",
      "What is the V122I or V30M variant and what does it mean for me?",
    ],
  },
  ATTRv: {
    general: [
      "Which TTR gene variant do I carry and what does it mean for my prognosis?",
      "Should my children and siblings be tested for the TTR variant?",
      "Am I eligible for RNA-interference therapies like patisiran or vutrisiran?",
      "Am I eligible for Attruby (acoramidis) for my cardiac involvement?",
      "What genetic counseling resources are available to my family?",
      "Are there prevention trials I can join, like ACT-EARLY?",
    ],
    cardiac: [
      "What is my current NT-proBNP and echocardiogram showing?",
      "How quickly is my cardiomyopathy progressing?",
      "Should I consider a referral to an ATTR center of excellence?",
    ],
    neurological: [
      "How severe is my polyneuropathy and what is the trend?",
      "Am I a candidate for liver transplant to stop TTR production?",
      "What is the best treatment for my hereditary neuropathy?",
    ],
  },
  ATTRwt: {
    general: [
      "Am I eligible for Attruby (acoramidis) or tafamidis?",
      "How quickly is wild-type ATTR typically progressing?",
      "What is my current NT-proBNP and what is the trend?",
      "Should I see a cardiac amyloidosis specialist?",
      "Are there clinical trials available for wild-type ATTR-CM?",
      "Do I need genetic testing even though wild-type is not inherited?",
    ],
    cardiac: [
      "What stage of ATTR-CM am I in?",
      "Should I have a repeat bone scintigraphy scan?",
      "How often should I have echocardiograms?",
      "Am I at risk for heart block or arrhythmia?",
      "Should I be on anticoagulation therapy?",
    ],
  },
  AA: {
    general: [
      "What is my current serum amyloid A (SAA) level?",
      "Is my underlying inflammatory condition well controlled?",
      "What is the target SAA level we should aim for?",
      "Has the proteinuria improved since starting treatment?",
      "Should I see a nephrologist as well as my rheumatologist?",
      "Are there clinical trials for AA amyloidosis I should consider?",
    ],
    renal: [
      "What is my current 24-hour urine protein and eGFR?",
      "Is my kidney function stable, improving, or declining?",
      "What is the timeline for potential kidney recovery if SAA is controlled?",
      "At what point should I start dialysis planning?",
      "Am I a candidate for kidney transplant?",
    ],
  },
};

const GENERAL_QUESTIONS = [
  "What should I do if my symptoms worsen between appointments?",
  "Who should I call in an emergency related to my amyloidosis?",
  "Are there any support groups or patient organizations you recommend?",
  "What dietary changes should I make?",
  "Can I exercise and if so, what type and how much?",
  "Are there any medications — including over-the-counter — I should avoid?",
  "Should I get a flu shot or other vaccines?",
  "What is my prognosis and what factors affect it?",
];

// ── Helpers ───────────────────────────────────────────────────────────────────

const getAmyloidKey = (amyloidType) => {
  if (!amyloidType) return null;
  if (amyloidType.includes('ATTRv') || amyloidType.includes('Hereditary')) return 'ATTRv';
  if (amyloidType.includes('ATTRwt') || amyloidType.includes('Wild')) return 'ATTRwt';
  if (amyloidType.includes('ATTR')) return 'ATTR';
  if (amyloidType.includes('AL') || amyloidType.includes('Light')) return 'AL';
  if (amyloidType.includes('AA') || amyloidType.includes('Secondary')) return 'AA';
  return null;
};

const parseOrgans = (affectedOrgans) => {
  if (!affectedOrgans) return [];
  if (Array.isArray(affectedOrgans)) return affectedOrgans.map(o => o.toLowerCase());
  return affectedOrgans.split(',').map(o => o.trim().toLowerCase());
};

const getOrganKey = (organ) => {
  if (organ.includes('card') || organ.includes('heart')) return 'cardiac';
  if (organ.includes('renal') || organ.includes('kidney')) return 'renal';
  if (organ.includes('neuro') || organ.includes('nerve')) return 'neurological';
  if (organ.includes('liver') || organ.includes('hepat')) return 'liver';
  return null;
};

// ── Main Component ────────────────────────────────────────────────────────────

const DoctorDiscussionGuide = () => {
  const navigate = useNavigate();
  const { isAuthenticated, loginWithRedirect } = useAuth0();

  const [patient, setPatient] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState(null);

  // Guide state
  const [checkedQuestions, setCheckedQuestions] = useState({});
  const [myQuestions, setMyQuestions] = useState(['', '', '']);
  const [mySymptoms, setMySymptoms] = useState('');
  const [myConcerns, setMyConcerns] = useState('');
  const [visitDate, setVisitDate] = useState('');
  const [doctorName, setDoctorName] = useState('');

  useEffect(() => {
    if (!isAuthenticated) {
      loginWithRedirect({ appState: { returnTo: '/my-health/doctor-discussion-guide' } });
    } else {
      loadPatient();
    }
  }, [isAuthenticated]);

  const loadPatient = async () => {
    try {
      setLoading(true);
      const res = await api.get('/v1/patients/me');
      if (res.data) {
        setPatient(res.data);
        // Load saved guide state if exists
        const saved = res.data.doctorGuide;
        if (saved) {
          try {
            const parsed = JSON.parse(saved);
            if (parsed.checkedQuestions) setCheckedQuestions(parsed.checkedQuestions);
            if (parsed.myQuestions) setMyQuestions(parsed.myQuestions);
            if (parsed.mySymptoms) setMySymptoms(parsed.mySymptoms);
            if (parsed.myConcerns) setMyConcerns(parsed.myConcerns);
            if (parsed.visitDate) setVisitDate(parsed.visitDate);
            if (parsed.doctorName) setDoctorName(parsed.doctorName);
          } catch (e) {}
        }
      }
    } catch (err) {
      setError('Failed to load patient profile');
    } finally {
      setLoading(false);
    }
  };

  // Generate personalized questions
  const getPersonalizedQuestions = () => {
    if (!patient) return { typeQuestions: [], organQuestions: [], generalQuestions: GENERAL_QUESTIONS };

    const amyloidKey = getAmyloidKey(patient.amyloidType);
    const organs = parseOrgans(patient.affectedOrgans);
    const bank = amyloidKey ? QUESTION_BANK[amyloidKey] : null;

    const typeQuestions = bank?.general || [];
    const organQuestions = [];

    organs.forEach(organ => {
      const key = getOrganKey(organ);
      if (key && bank?.[key]) {
        organQuestions.push(...bank[key].map(q => ({ organ: organ, question: q })));
      }
    });

    return { typeQuestions, organQuestions, generalQuestions: GENERAL_QUESTIONS };
  };

  const { typeQuestions, organQuestions, generalQuestions } = getPersonalizedQuestions();

  const toggleQuestion = (id) => {
    setCheckedQuestions(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      const guideData = {
        checkedQuestions,
        myQuestions,
        mySymptoms,
        myConcerns,
        visitDate,
        doctorName,
        savedAt: new Date().toISOString(),
      };
      await api.put(`/v1/patients/${patient.id}`, {
        ...patient,
        doctorGuide: JSON.stringify(guideData),
      });
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (err) {
      setError('Failed to save guide');
    } finally {
      setSaving(false);
    }
  };

  const handlePrint = () => {
    window.print();
  };

  const getCheckedCount = () => Object.values(checkedQuestions).filter(Boolean).length;

  if (!isAuthenticated || loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ bgcolor: '#f8fafc', minHeight: '100vh', pb: 8 }}>
      {/* Print styles */}
      <style>{`
        @media print {
          .no-print { display: none !important; }
          .MuiAccordion-root { break-inside: avoid; }
          body { background: white; }
          .print-header { display: block !important; }
        }
        .print-header { display: none; }
      `}</style>

      {/* Header */}
      <Box sx={{
        background: 'linear-gradient(135deg, #1A5EA8 0%, #0B2545 100%)',
        color: 'white',
        py: 5,
        px: 3,
        mb: 4,
      }} className="no-print">
        <Container maxWidth="lg">
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 2 }}>
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <MedicalServices />
                <Typography variant="overline" sx={{ color: 'rgba(255,255,255,0.7)', letterSpacing: 2 }}>
                  My Health Tools
                </Typography>
              </Box>
              <Typography variant="h3" fontWeight={700} gutterBottom>
                Doctor Discussion Guide
              </Typography>
              <Typography variant="body1" sx={{ color: 'rgba(255,255,255,0.8)', maxWidth: 560 }}>
                Personalized questions and talking points for your next appointment —
                based on your amyloid type and affected organs.
              </Typography>
              {patient?.amyloidType && (
                <Box sx={{ mt: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  <Chip label={patient.amyloidType} sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white' }} />
                  {parseOrgans(patient.affectedOrgans).map(organ => (
                    <Chip key={organ} label={organ} sx={{ bgcolor: 'rgba(255,255,255,0.15)', color: 'white' }} size="small" />
                  ))}
                </Box>
              )}
            </Box>
            <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
              <Button
                variant="outlined"
                startIcon={<Print />}
                onClick={handlePrint}
                sx={{ color: 'white', borderColor: 'rgba(255,255,255,0.4)', '&:hover': { borderColor: 'white', bgcolor: 'rgba(255,255,255,0.1)' } }}
              >
                Print / PDF
              </Button>
              <Button
                variant="contained"
                startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <Save />}
                onClick={handleSave}
                disabled={saving}
                sx={{ bgcolor: 'white', color: '#1A5EA8', fontWeight: 700, '&:hover': { bgcolor: 'rgba(255,255,255,0.9)' } }}
              >
                {saving ? 'Saving...' : saved ? '✓ Saved!' : 'Save Guide'}
              </Button>
            </Box>
          </Box>
        </Container>
      </Box>

      <Container maxWidth="lg">
        {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}

        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '1fr 320px' }, gap: 3 }}>

          {/* LEFT — Questions */}
          <Box>
            {/* Visit Details */}
            <Paper elevation={0} sx={{ p: 3, mb: 3, borderRadius: 3, border: '1px solid #e2e8f0' }}>
              <Typography variant="h6" fontWeight={700} gutterBottom sx={{ color: '#0D2B45' }}>
                📅 Appointment Details
              </Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, mt: 2 }}>
                <TextField
                  label="Visit Date"
                  type="date"
                  InputLabelProps={{ shrink: true }}
                  value={visitDate}
                  onChange={e => setVisitDate(e.target.value)}
                  size="small"
                />
                <TextField
                  label="Doctor's Name"
                  value={doctorName}
                  onChange={e => setDoctorName(e.target.value)}
                  size="small"
                  placeholder="Dr. Smith"
                />
              </Box>
            </Paper>

            {/* Type-specific questions */}
            {typeQuestions.length > 0 && (
              <Accordion defaultExpanded elevation={0} sx={{ mb: 2, borderRadius: '12px !important', border: '1px solid #e2e8f0', '&:before': { display: 'none' } }}>
                <AccordionSummary expandIcon={<ExpandMore />} sx={{ px: 3, py: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#1A5EA8' }} />
                    <Typography fontWeight={700} color="#0D2B45">
                      Questions About My {patient?.amyloidType}
                    </Typography>
                    <Chip
                      label={`${typeQuestions.filter((_, i) => checkedQuestions[`type-${i}`]).length}/${typeQuestions.length} selected`}
                      size="small"
                      sx={{ bgcolor: '#EBF5FF', color: '#1B6CA8', fontSize: 11 }}
                    />
                  </Box>
                </AccordionSummary>
                <AccordionDetails sx={{ px: 3, pb: 3 }}>
                  {typeQuestions.map((q, i) => (
                    <FormControlLabel
                      key={i}
                      control={
                        <Checkbox
                          checked={!!checkedQuestions[`type-${i}`]}
                          onChange={() => toggleQuestion(`type-${i}`)}
                          sx={{ color: '#1A5EA8', '&.Mui-checked': { color: '#1A5EA8' } }}
                        />
                      }
                      label={<Typography variant="body2" sx={{ py: 0.5 }}>{q}</Typography>}
                      sx={{ display: 'flex', alignItems: 'flex-start', mb: 0.5, ml: 0 }}
                    />
                  ))}
                </AccordionDetails>
              </Accordion>
            )}

            {/* Organ-specific questions */}
            {organQuestions.length > 0 && (
              <Accordion defaultExpanded elevation={0} sx={{ mb: 2, borderRadius: '12px !important', border: '1px solid #e2e8f0', '&:before': { display: 'none' } }}>
                <AccordionSummary expandIcon={<ExpandMore />} sx={{ px: 3, py: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#10b981' }} />
                    <Typography fontWeight={700} color="#0D2B45">
                      Questions About My Affected Organs
                    </Typography>
                    <Chip
                      label={`${organQuestions.filter((_, i) => checkedQuestions[`organ-${i}`]).length}/${organQuestions.length} selected`}
                      size="small"
                      sx={{ bgcolor: '#E6F4EA', color: '#137333', fontSize: 11 }}
                    />
                  </Box>
                </AccordionSummary>
                <AccordionDetails sx={{ px: 3, pb: 3 }}>
                  {organQuestions.map(({ organ, question }, i) => (
                    <Box key={i}>
                      {(i === 0 || organQuestions[i-1].organ !== organ) && (
                        <Typography variant="caption" sx={{ color: '#1A5EA8', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1, display: 'block', mt: i > 0 ? 2 : 0, mb: 0.5 }}>
                          {organ}
                        </Typography>
                      )}
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={!!checkedQuestions[`organ-${i}`]}
                            onChange={() => toggleQuestion(`organ-${i}`)}
                            sx={{ color: '#10b981', '&.Mui-checked': { color: '#10b981' } }}
                          />
                        }
                        label={<Typography variant="body2" sx={{ py: 0.5 }}>{question}</Typography>}
                        sx={{ display: 'flex', alignItems: 'flex-start', mb: 0.5, ml: 0 }}
                      />
                    </Box>
                  ))}
                </AccordionDetails>
              </Accordion>
            )}

            {/* General questions */}
            <Accordion elevation={0} sx={{ mb: 2, borderRadius: '12px !important', border: '1px solid #e2e8f0', '&:before': { display: 'none' } }}>
              <AccordionSummary expandIcon={<ExpandMore />} sx={{ px: 3, py: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#f59e0b' }} />
                  <Typography fontWeight={700} color="#0D2B45">General Questions</Typography>
                </Box>
              </AccordionSummary>
              <AccordionDetails sx={{ px: 3, pb: 3 }}>
                {generalQuestions.map((q, i) => (
                  <FormControlLabel
                    key={i}
                    control={
                      <Checkbox
                        checked={!!checkedQuestions[`general-${i}`]}
                        onChange={() => toggleQuestion(`general-${i}`)}
                        sx={{ color: '#f59e0b', '&.Mui-checked': { color: '#f59e0b' } }}
                      />
                    }
                    label={<Typography variant="body2" sx={{ py: 0.5 }}>{q}</Typography>}
                    sx={{ display: 'flex', alignItems: 'flex-start', mb: 0.5, ml: 0 }}
                  />
                ))}
              </AccordionDetails>
            </Accordion>

            {/* My Symptoms */}
            <Paper elevation={0} sx={{ p: 3, mb: 3, borderRadius: 3, border: '1px solid #e2e8f0' }}>
              <Typography variant="h6" fontWeight={700} gutterBottom sx={{ color: '#0D2B45' }}>
                🩺 My Current Symptoms
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                What symptoms have you experienced since your last visit? Be specific — include when they started and how severe.
              </Typography>
              <TextField
                fullWidth
                multiline
                rows={4}
                placeholder="e.g., Increased shortness of breath when climbing stairs over the past 2 weeks. Ankle swelling most evenings. Fatigue seems worse in the afternoons..."
                value={mySymptoms}
                onChange={e => setMySymptoms(e.target.value)}
              />
            </Paper>

            {/* My Concerns */}
            <Paper elevation={0} sx={{ p: 3, mb: 3, borderRadius: 3, border: '1px solid #e2e8f0' }}>
              <Typography variant="h6" fontWeight={700} gutterBottom sx={{ color: '#0D2B45' }}>
                💬 My Personal Concerns
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                What's worrying you most? What do you most want to discuss with your doctor today?
              </Typography>
              <TextField
                fullWidth
                multiline
                rows={3}
                placeholder="e.g., I'm worried about starting dialysis. I want to understand if I'm eligible for a clinical trial. I'd like to discuss my prognosis..."
                value={myConcerns}
                onChange={e => setMyConcerns(e.target.value)}
              />
            </Paper>

            {/* My Own Questions */}
            <Paper elevation={0} sx={{ p: 3, mb: 3, borderRadius: 3, border: '1px solid #e2e8f0' }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6" fontWeight={700} sx={{ color: '#0D2B45' }}>
                  ✍️ My Own Questions
                </Typography>
                <Button
                  size="small"
                  startIcon={<Add />}
                  onClick={() => setMyQuestions(prev => [...prev, ''])}
                  sx={{ color: '#1A5EA8' }}
                >
                  Add Question
                </Button>
              </Box>
              {myQuestions.map((q, i) => (
                <Box key={i} sx={{ display: 'flex', gap: 1, mb: 1.5 }}>
                  <TextField
                    fullWidth
                    size="small"
                    placeholder={`Question ${i + 1}...`}
                    value={q}
                    onChange={e => {
                      const updated = [...myQuestions];
                      updated[i] = e.target.value;
                      setMyQuestions(updated);
                    }}
                  />
                  <IconButton
                    size="small"
                    onClick={() => setMyQuestions(prev => prev.filter((_, idx) => idx !== i))}
                    sx={{ color: '#9ca3af', '&:hover': { color: '#dc2626' } }}
                  >
                    <Delete fontSize="small" />
                  </IconButton>
                </Box>
              ))}
            </Paper>
          </Box>

          {/* RIGHT — Summary sidebar */}
          <Box>
            <Paper elevation={0} sx={{ p: 3, borderRadius: 3, border: '1px solid #e2e8f0', position: 'sticky', top: 24 }}>
              <Typography variant="h6" fontWeight={700} sx={{ color: '#0D2B45', mb: 3 }}>
                Your Guide Summary
              </Typography>

              {/* Patient info */}
              <Box sx={{ bgcolor: '#f8fafc', borderRadius: 2, p: 2, mb: 3 }}>
                <Typography variant="subtitle2" fontWeight={700} color="#0D2B45">
                  {patient?.firstName} {patient?.lastName}
                </Typography>
                {patient?.amyloidType && (
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                    {patient.amyloidType}
                  </Typography>
                )}
                {patient?.diagnosisDate && (
                  <Typography variant="body2" color="text.secondary">
                    Diagnosed: {new Date(patient.diagnosisDate).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
                  </Typography>
                )}
                {visitDate && (
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1, fontWeight: 600 }}>
                    Next visit: {new Date(visitDate).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
                  </Typography>
                )}
              </Box>

              {/* Stats */}
              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, mb: 3 }}>
                <Box sx={{ textAlign: 'center', p: 2, bgcolor: '#EBF5FF', borderRadius: 2 }}>
                  <Typography variant="h4" fontWeight={700} sx={{ color: '#1A5EA8' }}>
                    {getCheckedCount()}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">Questions selected</Typography>
                </Box>
                <Box sx={{ textAlign: 'center', p: 2, bgcolor: '#E6F4EA', borderRadius: 2 }}>
                  <Typography variant="h4" fontWeight={700} sx={{ color: '#10b981' }}>
                    {myQuestions.filter(q => q.trim()).length}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">My own questions</Typography>
                </Box>
              </Box>

              <Divider sx={{ mb: 3 }} />

              {/* Tips */}
              <Typography variant="subtitle2" fontWeight={700} color="#0D2B45" gutterBottom>
                Tips for Your Visit
              </Typography>
              {[
                'Bring a trusted person with you to take notes',
                'Ask for test results in writing',
                'Don\'t leave without understanding next steps',
                'Ask about clinical trials at every visit',
                'It\'s okay to ask for a second opinion',
              ].map((tip, i) => (
                <Box key={i} sx={{ display: 'flex', gap: 1, mb: 1 }}>
                  <Typography sx={{ color: '#1A5EA8', fontWeight: 700, flexShrink: 0 }}>→</Typography>
                  <Typography variant="body2" color="text.secondary">{tip}</Typography>
                </Box>
              ))}

              <Box sx={{ mt: 3, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                <Button
                  fullWidth
                  variant="contained"
                  startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <Save />}
                  onClick={handleSave}
                  disabled={saving}
                  sx={{ bgcolor: '#1A5EA8', '&:hover': { bgcolor: '#5568d3' } }}
                >
                  {saving ? 'Saving...' : saved ? '✓ Saved!' : 'Save Guide'}
                </Button>
                <Button
                  fullWidth
                  variant="outlined"
                  startIcon={<Print />}
                  onClick={handlePrint}
                  sx={{ borderColor: '#1A5EA8', color: '#1A5EA8' }}
                >
                  Print / Save PDF
                </Button>
              </Box>
            </Paper>
          </Box>
        </Box>
      </Container>
    </Box>
  );
};

export default DoctorDiscussionGuide;